import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { keyframes, styled, alpha } from '@mui/material/styles';
import type { VenueTypeCode } from '../../features/landing/constants/venueTypes';
import { ThemedLoadingAnimation } from '../loading/themed';

interface LoadingSpinnerProps {
  size?: 'sm' | 'md' | 'lg' | 'xl';
  fullScreen?: boolean;
  message?: string;
  variant?: 'default' | 'pulse' | 'dots' | 'themed';
  venueType?: VenueTypeCode;
}

const sizeMap = {
  sm: 24,
  md: 40,
  lg: 56,
  xl: 72,
};

const pulse = keyframes`
  0%, 100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.5;
    transform: scale(0.95);
  }
`;

const dotPulse = keyframes`
  0%, 80%, 100% {
    transform: scale(0);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
`;

const SpinnerContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'fullScreen',
})<{ fullScreen?: boolean }>(({ theme, fullScreen }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  gap: 16,
  ...(fullScreen && {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: alpha(theme.palette.background.default, 0.95),
    zIndex: theme.zIndex.modal,
  }),
  ...(!fullScreen && {
    minHeight: 200,
  }),
}));

const PulseSpinner = styled(Box)(({ theme }) => ({
  width: 48,
  height: 48,
  borderRadius: '50%',
  backgroundColor: alpha(theme.palette.text.primary, 0.08),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  animation: `${pulse} 1.5s ease-in-out infinite`,
  '&::before': {
    content: '""',
    width: 24,
    height: 24,
    borderRadius: '50%',
    backgroundColor: theme.palette.text.primary,
  },
}));

const DotsContainer = styled(Box)({
  display: 'flex',
  gap: 8,
});

const Dot = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'delay',
})<{ delay: number }>(({ theme, delay }) => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  backgroundColor: theme.palette.text.primary,
  animation: `${dotPulse} 1.4s ease-in-out infinite`,
  animationDelay: `${delay}s`,
}));

const StyledProgress = styled(CircularProgress)(({ theme }) => ({
  color: theme.palette.text.primary,
}));

export function LoadingSpinner({
  size = 'md',
  fullScreen = false,
  message,
  variant = 'default',
  venueType,
}: LoadingSpinnerProps) {
  const spinnerSize = sizeMap[size];

  if (variant === 'themed') {
    return (
      <SpinnerContainer fullScreen={fullScreen}>
        <ThemedLoadingAnimation venueType={venueType} message={message} />
      </SpinnerContainer>
    );
  }

  return (
    <SpinnerContainer fullScreen={fullScreen}>
      {variant === 'default' && <StyledProgress size={spinnerSize} thickness={4} />}
      {variant === 'pulse' && <PulseSpinner />}
      {variant === 'dots' && (
        <DotsContainer>
          <Dot delay={0} />
          <Dot delay={0.2} />
          <Dot delay={0.4} />
        </DotsContainer>
      )}
      {message && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            fontFamily: '"IBM Plex Sans", sans-serif',
            fontWeight: 500,
            letterSpacing: '0.02em',
          }}
        >
          {message}
        </Typography>
      )}
    </SpinnerContainer>
  );
}

// Skeleton variant for content loading
export { default as Skeleton } from '@mui/material/Skeleton';
